import { useEffect, useMemo, useState } from "react";

import { IPart, TitleCase, IPartCategoryDefault } from "../../models/Part";
import classNames from "../../utils/classNames";
import useNotification from "../notifications/useNotifications";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import usePartMutations from "../../data/usePartMutations";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import useOffers from "../../data/useOffers";
import { TextInputWithValue } from "../shared/Inputs/TextInputWithValue";
import toTitleCase from "../../utils/toTitleCase";
import usePartDefaultCategories from "../../data/usePartDefaultCategories";
import LoadingWheel from "../shared/LoadingWheel";
import LoadingPulse from "../shared/LoadingPulse";

interface EbayListingTitleProps {
  part?: IPart;
  title?: string;
  onTable?: boolean;
  register?: any;
  getValues?: any;
  setValue?: any;
  watch?: any;
  isTemplate?: boolean;
  registerName?: string;
  onlyTitle?: boolean;
}

type IDefaults = {
  label: string;
  value: any;
  mergeTag: string;
  dataClass: string;
  tailwindClass: string;
  preserveCase: boolean;
};

// Helper functions for title computation
const formatEngineCapacity = (engine: number | null | undefined) => {
  if (!engine) return engine;
  const rounded = Math.round(engine / 100) * 100;
  return (rounded / 1000).toFixed(1) + "L";
};

const formatSuffix = (
  value: string | number | null | undefined,
  suffix: string
) => {
  if (!value) return value;
  return value + suffix;
};

const formatPrefix = (
  value: string | number | null | undefined,
  prefix: string
) => {
  if (!value) return value;
  return prefix + value;
};

const escapeRegex = (str: string) =>
  str.replace(/[-\/\\^$*+?.()|[\]{}]/g, "\\$&");

const formatWordByTitleCase = (word: string, titleCase: TitleCase, preserveCase: boolean): string => {
  if (preserveCase) return word;
  switch (titleCase) {
    case TitleCase.Upper:
      return word.toUpperCase();
    case TitleCase.Sentence:
      return toTitleCase(word);
    case TitleCase["No Change"]:
    default:
      return word;
  }
};

const replaceMergeTags = (title: string, defaults: IDefaults[], titleCase: TitleCase): string => {
  let formatted = title;

  defaults.forEach((def) => {
    if (!def.mergeTag) return;

    const tagRegex = new RegExp(escapeRegex(def.mergeTag), "gi");

    if (def.value) {
      const valueStr = def.value.toString();
      const casedValue = formatWordByTitleCase(
        valueStr,
        titleCase,
        def.preserveCase
      );
      formatted = formatted.replace(tagRegex, casedValue);
    } else {
      formatted = formatted.replace(new RegExp(`${def.mergeTag}\\s?`, "gi"), "");
    }
  });

  return formatted;
};

/**
 * Builds the defaults array for title computation
 * @param part - The part to get values from
 * @param categoryDefaults - Optional category defaults
 * @param isTemplate - Whether in template mode (nullifies values if true)
 * @returns Array of default values with merge tags
 */
function buildDefaultsArray(
  part: IPart | undefined,
  categoryDefaults?: IPartCategoryDefault | null,
  isTemplate: boolean = false
): IDefaults[] {
  return [
    { label: "Category Name", value: isTemplate ? null : part?.category?.name ?? part?.title, mergeTag: "{{categoryName}}", dataClass: "data-yellow", tailwindClass: "bg-yellow-300", preserveCase: false },
    { label: "Category Alias", value: isTemplate ? null : categoryDefaults?.categoryAlias, mergeTag: "{{categoryAlias}}", dataClass: "data-yellow", tailwindClass: "bg-yellow-400", preserveCase: false },
    { label: "Year", value: isTemplate ? null : part?.vehicle?.dvlaYearOfManufacture, mergeTag: "{{year}}", dataClass: "data-green", tailwindClass: "bg-green-300", preserveCase: false },
    { label: "Make", value: isTemplate ? null : part?.vehicle?.combinedMake, mergeTag: "{{make}}", dataClass: "data-blue", tailwindClass: "bg-blue-300", preserveCase: false },
    { label: "Model", value: isTemplate ? null : part?.vehicle?.combinedModel, mergeTag: "{{model}}", dataClass: "data-orange", tailwindClass: "bg-orange-300", preserveCase: false },
    { label: "Short Model", value: isTemplate ? null : part?.vehicle?.modelRangeDescription, mergeTag: "{{shortModel}}", dataClass: "data-orange", tailwindClass: "bg-orange-400", preserveCase: false },
    { label: "MK", value: isTemplate ? null : formatPrefix(part?.vehicle?.mark, "MK"), mergeTag: "{{mk}}", dataClass: "data-lime", tailwindClass: "bg-lime-300", preserveCase: true },
    { label: "Series", value: isTemplate ? null : part?.vehicle?.modelSeries, mergeTag: "{{series}}", dataClass: "data-lime", tailwindClass: "bg-lime-300", preserveCase: true },
    { label: "Fuel Type", value: isTemplate ? null : part?.vehicle?.fuelType, mergeTag: "{{fuelType}}", dataClass: "data-teal", tailwindClass: "bg-teal-300", preserveCase: false },
    { label: "Door", value: isTemplate ? null : formatSuffix(part?.vehicle?.numberOfDoors, " Doors"), mergeTag: "{{door}}", dataClass: "data-cyan", tailwindClass: "bg-cyan-300", preserveCase: false },
    { label: "Body Style", value: isTemplate ? null : part?.vehicle?.bodyStyleDescription, mergeTag: "{{bodyStyle}}", dataClass: "data-indigo", tailwindClass: "bg-indigo-300", preserveCase: false },
    { label: "Engine Capacity", value: isTemplate ? null : formatEngineCapacity(part?.vehicle?.engineCapacity), mergeTag: "{{engineCapacity}}", dataClass: "data-slate", tailwindClass: "bg-slate-300", preserveCase: true },
    { label: "Engine Code", value: isTemplate ? null : part?.vehicle?.engineModelCode, mergeTag: "{{engineCode}}", dataClass: "data-purple", tailwindClass: "bg-purple-300", preserveCase: true },
    { label: "BHP", value: isTemplate ? null : formatSuffix(part?.vehicle?.maximumPowerBHP, " BHP"), mergeTag: "{{bhp}}", dataClass: "data-fuchsia", tailwindClass: "bg-fuchsia-300", preserveCase: true },
    { label: "No. Gears", value: isTemplate ? null : formatSuffix(part?.vehicle?.combinedForwardGears, " Speed"), mergeTag: "{{noGears}}", dataClass: "data-pink", tailwindClass: "bg-pink-300", preserveCase: false },
    { label: "Transmission", value: isTemplate ? null : part?.vehicle?.combinedTransmission, mergeTag: "{{transmission}}", dataClass: "data-rose", tailwindClass: "bg-rose-300", preserveCase: false },
    { label: "Mileage", value: isTemplate ? null : (part?.vehicle?.odometerInMiles === 0 ? null : part?.vehicle?.odometerInMiles), mergeTag: "{{mileage}}", dataClass: "data-amber", tailwindClass: "bg-amber-300", preserveCase: false },
    { label: "Colour", value: isTemplate ? null : part?.vehicle?.colourCurrent, mergeTag: "{{colour}}", dataClass: "data-amber", tailwindClass: "bg-amber-300", preserveCase: false },
    { label: "Colour Code", value: isTemplate ? null : part?.vehicle?.vinvisColourCode, mergeTag: "{{colourCode}}", dataClass: "data-amber", tailwindClass: "bg-amber-300", preserveCase: false },
    { label: "Tag Number", value: isTemplate ? null : part?.tagNumber, mergeTag: "{{tagNumber}}", dataClass: "data-green", tailwindClass: "bg-green-300", preserveCase: true },
    { label: "Part Number", value: isTemplate ? null : (part?.oePartNumber ?? part?.partNumber), mergeTag: "{{partNumber}}", dataClass: "data-blue", tailwindClass: "bg-blue-300", preserveCase: true },
  ];
}

/**
 * Computes the eBay listing title for a part based on merge tags and category defaults
 * @param part - The part to compute the title for
 * @param categoryDefaults - Optional category defaults (if not provided, will use part.category?.partCategoryDefaults[0])
 * @returns The computed title string
 */
export function computeEbayListingTitle(
  part: IPart,
  categoryDefaults?: IPartCategoryDefault | null
): string {
  if (!part) return "";

  // Use provided categoryDefaults or fall back to part's category defaults
  const defaults = categoryDefaults ?? part.category?.partCategoryDefaults?.[0] ?? null;
  
  // Prefer saved title if available
  const savedTitle = part.offers?.[0]?.title;
  
  // Determine the base title to use
  let targetTitle = savedTitle || `{{year}} {{make}} {{shortModel}} {{categoryName}}`;
  
  // Use category titleStructure if no saved title exists
  if (!savedTitle && defaults?.titleStructure) {
    targetTitle = defaults.titleStructure;
  }

  // Build defaults array using shared function
  const defaultsArray = buildDefaultsArray(part, defaults, false);

  // Get title case from category defaults
  const titleCase = (defaults?.titleCase as TitleCase) ?? TitleCase["No Change"];

  // Apply title case formatting to the entire title first
  let processed = formatWordByTitleCase(targetTitle, titleCase, false);

  // Replace merge tags with actual values
  const merged = replaceMergeTags(processed, defaultsArray, titleCase);

  // Clean up extra spaces
  return merged.trim().replace(/\s{2,}/g, " ");
}

export default function EbayListingTitle({
  part,
  title,
  onTable = false,
  register,
  getValues,
  setValue,
  isTemplate = false,
  registerName = "title",
  onlyTitle = false,
}: EbayListingTitleProps) {
  const { patchPart } = usePartMutations();
  const { defaultPartCategory } = usePartDefaultCategories({
    part: part ?? { categoryId: "" } as IPart,
  });
  const { createDefaultOffer, update } = useOffers();
  const { saveHandlers } = useDefaultCRUDHandlers("Part Title");
  const { addNotification } = useNotification();

  // Prefer an existing saved offer title when available (non-template mode)
  const savedTitle = !isTemplate ? part?.offers?.[0]?.title : undefined;

  const initialTitle = onTable
    ? title
    : getValues?.(registerName) ||
    savedTitle ||
    (isTemplate ? "{{year}} {{make}} {{shortModel}} {{categoryName}}" : "");

  const [currentTitle, setCurrentTitle] = useState<string>(initialTitle);

  const [titleLabel, setTitleLabel] = useState(
    `Listing Title ${currentTitle?.length > 0 ? `(${currentTitle?.length}/80)` : ""
    }`
  );

  const defaults = useMemo(() => 
    buildDefaultsArray(part, defaultPartCategory.data?.data, isTemplate),
    [part, defaultPartCategory.data, isTemplate]
  );

  const highlightDefaultsInTitle = (title: string, defaults: IDefaults[], isTemplate: boolean): string => {
    if (!title) return "";

    let highlighted = title;

    const allValues = defaults
      .filter((d) => (isTemplate ? d.mergeTag : d.value))
      .map((d) => (isTemplate ? d.mergeTag : d.value)!.toString());

    const containedValues = new Set<string>();
    allValues.forEach((v, i) =>
      allValues.forEach((o, j) => {
        if (i !== j && o.toLowerCase().includes(v.toLowerCase())) {
          containedValues.add(v);
        }
      })
    );

    defaults.forEach((def) => {
      const valueToUse = isTemplate ? def.mergeTag : def.value;
      if (!valueToUse) return;

      const valueStr = valueToUse.toString();

      if (containedValues.has(valueStr)) {
        const hasLonger = allValues.some(
          (o) =>
            o !== valueStr &&
            o.toLowerCase().includes(valueStr.toLowerCase()) &&
            highlighted.toLowerCase().includes(o.toLowerCase())
        );
        if (hasLonger) return;
      }

      const regex = new RegExp(escapeRegex(valueStr), "gi");
      if (regex.test(highlighted)) {
        highlighted = highlighted
          .replace(regex, `<span ${def.dataClass}>${valueStr}</span>`);
      }
    });

    return highlighted;
  };


  const [rawTitle, setRawTitle] = useState<string>(
    isTemplate ? highlightDefaultsInTitle(initialTitle, defaults, true) : initialTitle
  );



  const handleSetValue = (name: string, value: string) => {
    const categoryDefaults = defaultPartCategory.data?.data;
    const titleCase =
      (categoryDefaults?.titleCase as TitleCase) ?? TitleCase["No Change"];

    let processed = value;

    if (isTemplate) {
      // Template mode: keep merge tags intact, don't replace
      const highlighted = highlightDefaultsInTitle(processed, defaults, true);
      setCurrentTitle(processed);
      setRawTitle(highlighted);
      setTitleLabel(
        `Listing Title ${processed?.length ? `(${processed.length}/80)` : ""}`
      );
      if (!onTable) setValue?.(name, processed);
      return;
    }

    let preCased = formatWordByTitleCase(processed, titleCase, false);

    const merged = replaceMergeTags(preCased, defaults, titleCase);

    const highlighted = highlightDefaultsInTitle(merged, defaults, false);

    setCurrentTitle(merged);
    setRawTitle(highlighted);
    setTitleLabel(
      `Listing Title ${merged?.length ? `(${merged.length}/80)` : ""}`
    );
    if (!onTable) setValue?.(name, merged);
  };


  const changeTitle = (def: IDefaults) => {
    let newTitle = currentTitle || "";
    const valueToUse = isTemplate ? def.mergeTag : def.value;

    if (!valueToUse) {
      addNotification({
        variant: "info",
        primaryText: "No value found",
      });
      return;
    }

    const word = valueToUse.toString();
    const escapedWord = word.replace(/[-\/\\^$*+?.()|[\]{}]/g, "\\$&");
    const regex = new RegExp(escapedWord, "i");

    if (regex.test(newTitle)) {
      newTitle = newTitle
        .replace(regex, "")
        .trim()
        .replace(/\s{2,}/g, " ");
    } else {
      newTitle = `${newTitle} ${word}`.trim().replace(/\s{2,}/g, " ");
    }

    handleSetValue(registerName, newTitle);
  };

  const handleSaveTitle = () => {
    if (isTemplate || !part) return;

    let offer = part.offers?.[0];
    let updatedOffer = {
      ...offer,
      title: currentTitle,
    };

    if (offer) {
      update.mutate(updatedOffer, {
        ...saveHandlers,
        onSuccess: () => {
          saveHandlers.onSuccess();
          offer.title = currentTitle;
        },
      });
    } else {
      createDefaultOffer.mutate(
        { partId: part.id, body: updatedOffer },
        saveHandlers
      );
    }
  };

  useEffect(() => {
    if (defaultPartCategory.isLoading) return;

    // Template mode: keep merge tags intact
    if (isTemplate) {
      let targetTitle = currentTitle || `{{year}} {{make}} {{shortModel}} {{categoryName}}`;

      const catDefaults = defaultPartCategory.data?.data;
      if (catDefaults?.titleStructure) targetTitle = catDefaults.titleStructure;

      setCurrentTitle(targetTitle);
      setRawTitle(highlightDefaultsInTitle(targetTitle, defaults, true));
      setTitleLabel(
        `Listing Title ${targetTitle?.length ? `(${targetTitle.length}/80)` : ""}`
      );
      if (!onTable) setValue?.(registerName, targetTitle);
      return;
    }

    // Non-template mode
    // Prefer current or saved title; only fall back to category defaults when neither exists
    let targetTitle = currentTitle || savedTitle || `{{year}} {{make}} {{shortModel}} {{categoryName}}`;

    const catDefaults = defaultPartCategory.data?.data;
    if (!savedTitle && catDefaults?.titleStructure) {
      targetTitle = catDefaults.titleStructure;
    }

    handleSetValue(registerName, targetTitle);
  }, [defaults, defaultPartCategory.data, defaultPartCategory.isLoading, isTemplate, savedTitle]);

  if (defaultPartCategory.isFetching && onlyTitle) {
    return <LoadingPulse />;
  }

  if (defaultPartCategory.isFetching) {
    return <LoadingWheel />;
  }

  if (onlyTitle) {
    return (
      <div className="flex gap-x-1">
        <h2 className="font-semibold">{titleLabel}: </h2> <span>{currentTitle}</span>
      </div>
    );
  }

  return (
    <>
      {/* Defaults grid */}
      <div className={classNames(onTable ? "xl:grid-cols-3" : "xl:grid-cols-5", "col-span-2 grid grid-cols-2 md:grid-cols-3 ")}>
        {defaults.map((def, i) => {
          const valueToUse = isTemplate ? def.mergeTag : def.value;
          const exists =
            valueToUse &&
            currentTitle
              ?.toLowerCase()
              .includes(valueToUse.toString().toLowerCase());

          return (
            <div
              key={i}
              className={classNames(
                exists ? def.tailwindClass : "",
                " text-center border border-gray-300 p-2 cursor-pointer hover:bg-gray-200 select-none"
              )}
              onClick={() => changeTitle(def)}
            >
              <span>{def.label}</span>
            </div>
          );
        })}
      </div>

      {register ? (
        <TextInputWithValue
          label={titleLabel}
          register={register ?? {}}
          registerName={registerName}
          cols="sm:col-span-2"
          disabled={part && part.soldDate !== null}
          value={currentTitle}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            handleSetValue(registerName, e.target.value)
          }
        />
      ) : (
        <div className="w-full flex gap-x-4 py-2">
          <div className={classNames(onTable ? "w-full" : "w-1/2", "flex flex-col gap-y-2")}>
            <label className="text-sm font-medium text-gray-500">
              Listing Title{" "} {currentTitle?.length > 0 ? `(${currentTitle?.length}/80)` : ""}
            </label>
            <input
              type="text"
              className="w-full flex-1 text-sm text-gray-900 border-1 border-gray-400"
              value={currentTitle}
              onChange={(e) => handleSetValue(registerName, e.target.value)}
            />
          </div>
          {!isTemplate && (
            <div className="pt-7">
              <ButtonInput
                label="Save"
                isSubmit={false}
                onClick={() => handleSaveTitle()}
                classes="py-3"
              />
            </div>
          )}
        </div>
      )}

      {/* Raw highlighted preview */}
      <div
        className="block text-sm col-span-2 [&>[data-green]]:bg-green-300 [&>[data-lime]]:bg-lime-300 [&>[data-teal]]:bg-teal-300 [&>[data-cyan]]:bg-cyan-300 [&>[data-indigo]]:bg-indigo-300 [&>[data-slate]]:bg-slate-300 [&>[data-purple]]:bg-purple-300 [&>[data-fuchsia]]:bg-fuchsia-300 [&>[data-pink]]:bg-pink-300 [&>[data-rose]]:bg-rose-300 [&>[data-amber]]:bg-amber-300 [&>[data-blue]]:bg-blue-300 [&>[data-orange]]:bg-orange-300 [&>[data-yellow]]:bg-yellow-300"
        dangerouslySetInnerHTML={{ __html: rawTitle }}
      />
    </>
  );
}
